import { Injectable } from '@angular/core';
import { CommonService } from './common.service';
import { MktContentService } from 'src/app/core/services';

@Injectable({
  providedIn: 'root'
})
export class QPStoreService {

  // CONSTANTS
  public readonly PDP_EAP_QS_COOKIE = 'PDP_EAP_QS';
  public readonly MPP_EAP_QS_COOKIE = 'MPP_EAP_QS';
  public readonly EAP_QS_EXPIRY_HOURS = 4;
  public readonly PAGE_TYPE_MPP = 'MPP';
  public readonly PAGE_TYPE_PDP = 'PDP';

  public pagePDPEAPQueryParams: any = {};
  public pageMPPEAPQueryParams: any = {};
  public allowedPDPQueryParams = [];
  public allowedMPPQueryParams = [];

  constructor(private commonService: CommonService,
    private _mktContentService: MktContentService) {}

  public setEAPPageQueryParams(pageQueryParams: any, pageType = this.PAGE_TYPE_PDP): void {
    const params = { ...pageQueryParams };
    if (!params || Object.keys(params).length === 0) {
      console.warn('No page query params to set');
      return;
    }

    // Load allowed query params from config
    const routeConfigContent = this._mktContentService.get('Branding', 'copy', 'route_config', undefined);
    const allowedParamsKey = (pageType === this.PAGE_TYPE_MPP ? 'allowedMPPQueryParams' : 'allowedPDPQueryParams');
    const parsedParams = JSON.parse(routeConfigContent?.[allowedParamsKey] || '[]');
    if (pageType === this.PAGE_TYPE_MPP) {
      this.allowedMPPQueryParams = parsedParams;
    } else {
      this.allowedPDPQueryParams = parsedParams;
    }    

    // convert if afc params comes in upper case to lower case
    if(!!params?.AFC) {
      params['afc'] = params.AFC;
      delete params.AFC;
    }
    
    // Filter only allowed query params
    const eapQP = this.getAllowedQPData(params, pageType);

    if (!eapQP?.cmp || Object.keys(eapQP).length === 0) {
      console.warn('No expected page query params to set, so reading from previous cookie if any');
      return;
    }

    eapQP.cmp = (eapQP.cmp as string)?.replaceAll("~", "|");
    if (pageType === this.PAGE_TYPE_MPP) {
      this.pageMPPEAPQueryParams = eapQP;
    } else {
      this.pagePDPEAPQueryParams = eapQP;
    }
    
    //delete both the cookie as new parameter detected
    // check previous mpp cookie data is same as current, if same do not delete
    if(pageType === this.PAGE_TYPE_MPP) {
      const previousMPPQP = this.getPreviousQPData(this.MPP_EAP_QS_COOKIE, this.PAGE_TYPE_MPP);
      const isSameMPPParams = previousMPPQP.length > 0 && 
                            JSON.stringify(previousMPPQP.at(-1)) === JSON.stringify(eapQP);
      this.commonService.updateCookie(this.PDP_EAP_QS_COOKIE, null);
      if(!isSameMPPParams) {
        this.commonService.updateCookie(this.MPP_EAP_QS_COOKIE, null);
      }
    } else {
      const previousPDPQP = this.getPreviousQPData(this.PDP_EAP_QS_COOKIE, this.PAGE_TYPE_PDP);
      const isSamePDPParams = previousPDPQP.length > 0 && 
                            JSON.stringify(previousPDPQP.at(-1)) === JSON.stringify(eapQP);
      this.commonService.updateCookie(this.MPP_EAP_QS_COOKIE, null);
      if(!isSamePDPParams) {
        this.commonService.updateCookie(this.PDP_EAP_QS_COOKIE, null);
      }
    }
  }
  
  public setPDPQPCookie(pidPT: string, cookieName: string = this.PDP_EAP_QS_COOKIE, pageQueryParams: any = {}): string {
    if(this.pagePDPEAPQueryParams?.pidPT) { // Already EAP loaded
      pageQueryParams = this.pagePDPEAPQueryParams?.pidPT === pidPT ? this.pagePDPEAPQueryParams : (this.getExistingProductByPID(pidPT) || {});
    } else if (!!this.pagePDPEAPQueryParams && Object.keys(this.pagePDPEAPQueryParams)?.length > 0){ // First time load
      pageQueryParams = this.pagePDPEAPQueryParams;
    }

    // Check the params have allowed EAP parameters
    if ((!pageQueryParams || Object.keys(pageQueryParams)?.length === 0) || !this.isValidQPData(pageQueryParams)) {
      return this.getHashcodeFromCookie(pidPT);
    }
    
    let formattedQPs: any[] = this.getPreviousQPData(cookieName) || [];

    //Valid HKOP
    if(!!pageQueryParams?.HKOP && pageQueryParams?.HKOP?.length !== 64) {
      console.warn('Invalid HKOP received, cookie not updated');
      return this.getHashcodeFromCookie(pidPT) || pageQueryParams?.HKOP;
    }

    // check logic the same HKOP should be present in formattedQPs
    const hasHKOPWithOtherPID = formattedQPs?.some(qp => qp?.HKOP && qp.pidPT != pidPT && pageQueryParams?.HKOP && qp?.HKOP === pageQueryParams?.HKOP);
    if(hasHKOPWithOtherPID) {
      return pageQueryParams?.HKOP;
    }
    
    // No Update if there is no change in params for the same product
    const index = formattedQPs.findIndex(qp => qp?.pidPT === pidPT);
    if (index > -1) {
      const existingProduct = formattedQPs[index];
      const updatedProduct = { ...existingProduct, ...pageQueryParams, pidPT };
      
      // Check if there are actual changes
      const hasChanges = JSON.stringify(existingProduct) !== JSON.stringify(updatedProduct);
      if(hasChanges) {
        formattedQPs = [{ ...pageQueryParams, pidPT }];
      }
      else {
        console.warn('No changes in query params, cookie not updated');
        return this.getHashcodeFromCookie(pidPT);
      }
    } else { // Add new Product
      formattedQPs = [{ ...pageQueryParams, pidPT }];
    }
    // Set/reset cookie with new params
    this.commonService.updateCookie(this.MPP_EAP_QS_COOKIE, null); // Delete old MPP cookie on PDP EAP load
    this.setCookie(formattedQPs, cookieName);
    return pageQueryParams?.HKOP || '';
  }

  public setMPPQPCookie(cookieName: string=this.MPP_EAP_QS_COOKIE, pageQueryParams: any = {}): string {
    if(!!this.pageMPPEAPQueryParams && Object.keys(this.pageMPPEAPQueryParams)?.length > 0){ // First time load
      pageQueryParams = this.pageMPPEAPQueryParams;
    }
    else if (this.getPreviousQPData(cookieName, this.PAGE_TYPE_MPP)?.length > 0) { // Already EAP loaded
      pageQueryParams = this.getPreviousQPData(cookieName, this.PAGE_TYPE_MPP)?.at(-1) || {};
    }
    
    // Check if received params are same as previous
    const previousQPs = this.getPreviousQPData(cookieName, this.PAGE_TYPE_MPP);
    const isSameMPPParams = previousQPs.length > 0 && 
                          JSON.stringify(previousQPs.at(-1)) === JSON.stringify(pageQueryParams);
  
    // If params are empty or unchanged, return afc
    if ((!pageQueryParams || Object.keys(pageQueryParams)?.length === 0) || isSameMPPParams) {
      return pageQueryParams?.afc || this.getAfcCodeFromEAPCookie(this.PAGE_TYPE_PDP) || '';
    }
    
    // Set/reset cookie with new params
    this.commonService.updateCookie(this.PDP_EAP_QS_COOKIE, null); // delete old PDP cookie on MPP EAP load
    this.setCookie([pageQueryParams], cookieName);
    return pageQueryParams?.afc || '';
  }

  public buildQPString(baseURL: string, queryParams: string): string {
    let updatedQPs: any;
    const qps = new URLSearchParams(queryParams);
    const mppCookieData = (!!this.pageMPPEAPQueryParams && Object.keys(this.pageMPPEAPQueryParams).length > 0) ? 
                          this.pageMPPEAPQueryParams : this.getPreviousQPData(this.MPP_EAP_QS_COOKIE, this.PAGE_TYPE_MPP)?.at(-1) || {};

    // return the URL as received if no cookie is present
    if((!this.getPreviousQPData() || Object.keys(this.getPreviousQPData())?.length === 0) && (!mppCookieData || Object.keys(mppCookieData)?.length === 0)) {
      return `${baseURL}&${qps.toString()}`;
    }

    // get the existing product from cookie if it exists
    // First check the same product in PDP, if not found then get MPP EAP Params 
    const existingProduct: any = this.getExistingProductByPID(qps.get('pid'));
    let product = existingProduct || mppCookieData;

    // if the existing product is not found, use the last PDP product from cookie or an empty object
    if(!product || Object.keys(product)?.length === 0) {
      product = this.getPreviousQPData().at(-1) || {};
    }    
    if(!product || Object.keys(product)?.length === 0) {
      return `${baseURL}&${qps.toString()}`;
    }

    const { pidPT, HKOP, ...rest } = product;
    updatedQPs = { ...Object.fromEntries(qps.entries()), ...rest };

    // update HKOP only if the same product loads
    if(product?.pidPT == qps.get('pid') && !!HKOP) {
      updatedQPs = { ...updatedQPs, HKOP };
    }
    const qpEntries = Object.entries(updatedQPs)
        .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value as string || '')}`);
    return qpEntries.length > 0 ? `${baseURL}&${qpEntries.join('&')}` : `${baseURL}&${qps.toString()}`;
  }

  //get the existing product from cookie if it exists
  public getHashcodeFromCookie(pid: string): string {
    return this.getExistingProductByPID(pid)?.HKOP || '';
  }

  // delete both cookies
  public deleteEAPQPCookies(): void {
    this.commonService.updateCookie(this.PDP_EAP_QS_COOKIE, null);
    this.commonService.updateCookie(this.MPP_EAP_QS_COOKIE, null);
  }

  // Get the existing afc from cookie if it exists
  public getAfcCodeFromEAPCookie(pageType: string = this.PAGE_TYPE_MPP, pid = ''): string {
    const getAfc = (cookieName: string, type: string) => {
      const qpData = this.getPreviousQPData(cookieName, type);
      if (type === this.PAGE_TYPE_PDP) {
      return (
        this.pagePDPEAPQueryParams?.afc ||
        this.getExistingProductByPID(pid)?.afc ||
        ''
      );
      }
      return qpData.at(-1)?.afc || '';
    };

    const afcValue = pageType === this.PAGE_TYPE_PDP
      ? getAfc(this.PDP_EAP_QS_COOKIE, this.PAGE_TYPE_PDP) || getAfc(this.MPP_EAP_QS_COOKIE, this.PAGE_TYPE_MPP) 
      : getAfc(this.MPP_EAP_QS_COOKIE, this.PAGE_TYPE_MPP) || getAfc(this.PDP_EAP_QS_COOKIE, this.PAGE_TYPE_PDP);
      return afcValue || this.getPreviousQPData().at(-1)?.afc || '';
  }

  public getOfferListFromCookie(offerList): any {
    const products = this.getPreviousQPData();
    if(products?.length > 0){
      products?.forEach((product) => {
        if(!!product?.HKOP) { // Add offers only HKOP present in cookie
          offerList['cardOffers'].push({ type: '', 'hashCode': product?.HKOP, 'pid': product?.pidPT, 'offerDisplayType': '', });
          }
        });
      }
    return offerList;
    }

  private getPreviousQPData(cookieName: string = this.PDP_EAP_QS_COOKIE, pageType = this.PAGE_TYPE_PDP): any[] {
    const previousQP = this.commonService.getCookie(cookieName, true);
    return previousQP || [];
  }

  private isValidQPData(pageQP: any, pageType = this.PAGE_TYPE_PDP): boolean {
      const eapQP = this.getAllowedQPData(pageQP, pageType);
      // cookie generated only if cmp parameter present
      if (!eapQP?.cmp || Object.keys(eapQP)?.length === 0) {
          console.warn('No expected page query params to set');
          return false;
      }
      return true;
  }

  // Set cookie
  private setCookie(formattedQPs, cookieName): void {
     const cookieValue = JSON.stringify(formattedQPs || '[]');
     const expirationDate = new Date();
     expirationDate.setHours(expirationDate.getHours() + this.EAP_QS_EXPIRY_HOURS);
     this.commonService.updateCookie(cookieName, cookieValue, expirationDate);
  }

  public isExistingProduct = (pid: string) => this.getPreviousQPData().some((qp) => pid == qp.pidPT);

  private getExistingProductByPID = (pid: string) => (!!pid && this.getPreviousQPData().filter((qp) => pid == qp.pidPT)?.[0]) || null;

  private getAllowedQPData(pageQueryParams: any, pageType: string = this.PAGE_TYPE_PDP): Record<string, any> {
    const allowedParams = pageType === this.PAGE_TYPE_MPP ? this.allowedMPPQueryParams : this.allowedPDPQueryParams;
    if (!allowedParams || Object.keys(allowedParams)?.length < 1) return {};
    try {
      if (!pageQueryParams || typeof pageQueryParams !== 'object') return {};
      return Object.fromEntries(
        Object.entries(pageQueryParams).filter(([key]) => allowedParams.includes(key))
      );
    } catch (error) {
      console.error('Error processing pageQueryParams :', error);
      return {};
    }
  }

}
